/**
 * Determine if the node is readonly or should be hidden.
 * @param {object} securityModifiers - typically `hNode.securityModifier` defined by Blockly,
 * but may also be the hNode itself when access modifiers are flattened (camelCase keys).
 * @param {{
 *     allFeatureFlags?:Array<string>,
 *     "identity:role"?:{title:string}
 * }} context
 * @param {{hidden:boolean, readOnly:boolean}} parentAccessRestrictions - typically the access restrictions of the parent derived from this function
 * @returns {{hidden:boolean, readOnly:boolean}} - the access restrictions for this component
 */
module.exports = (securityModifiers = {}, context = {}, parentAccessRestrictions = {}) => {
    const toStringArray = value => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        if (typeof value === 'string') return [value];
        return [];
    };

    const uniqueStrings = values =>
        Array.from(new Set((values || []).flat().filter(v => typeof v === 'string' && v.length > 0)));

    const role = context?.['identity:role']?.title || 'DEITY';
    const allFeatureFlags = Array.isArray(context.allFeatureFlags) ? context.allFeatureFlags : [];
    const isDeity = role === 'DEITY';

    // Inherit restrictions from parent
    let hidden = parentAccessRestrictions.hidden || false;
    let readOnly = parentAccessRestrictions.readOnly || false;

    // Support both Blockly-style PascalCase keys and flattened camelCase keys.
    const addForFeatureFlags = uniqueStrings([
        ...toStringArray(securityModifiers.AddForFeatureFlag),
        ...toStringArray(securityModifiers.AddForFeatureFlags),
        ...toStringArray(securityModifiers.addForFeatureFlag),
        ...toStringArray(securityModifiers.addForFeatureFlags)
    ]);

    const removeForFeatureFlags = uniqueStrings([
        ...toStringArray(securityModifiers.RemoveForFeatureFlag),
        ...toStringArray(securityModifiers.RemoveForFeatureFlags),
        ...toStringArray(securityModifiers.removeForFeatureFlag),
        ...toStringArray(securityModifiers.removeForFeatureFlags)
    ]);

    const readOnlyFor = uniqueStrings([
        ...toStringArray(securityModifiers.ReadOnlyFor),
        ...toStringArray(securityModifiers.readOnlyFor)
    ]);

    const hiddenFor = uniqueStrings([
        ...toStringArray(securityModifiers.HiddenFor),
        ...toStringArray(securityModifiers.hiddenFor)
    ]);

    // Hidden rules
    // - DEITY is never hidden by HiddenFor or feature flags (preserve existing semantics).
    if (!isDeity) {
        if (addForFeatureFlags.length > 0) {
            const hasAnyRequiredFlag = addForFeatureFlags.some(ff => allFeatureFlags.includes(ff));
            // Hidden if none of the required feature flags are enabled
            hidden = hidden || !hasAnyRequiredFlag;
        }

        if (removeForFeatureFlags.length > 0) {
            const hasAnyRemovedFlag = removeForFeatureFlags.some(ff => allFeatureFlags.includes(ff));
            // Hidden if any of the removal feature flags are enabled
            hidden = hidden || hasAnyRemovedFlag;
        }

        if (hiddenFor.length > 0) {
            hidden = hidden || hiddenFor.includes(role);
        }
    }

    // ReadOnly rules (applies even for DEITY, but with special semantics)
    if (readOnlyFor.length > 0) {
        if (isDeity) {
            // Only read-only for DEITY if it's read-only for (at least) the main interactive roles.
            readOnly =
                readOnly ||
                (readOnlyFor.includes('USER') && readOnlyFor.includes('ADMIN') && readOnlyFor.includes('SUPPORT'));
        } else {
            readOnly = readOnly || readOnlyFor.includes(role);
        }
    }

    return { hidden: !!hidden, readOnly: !!readOnly };
};
